import type { ScaleResult } from '@core-api/presentation/types/response';
import type { Question } from '@mentimeter/http-clients';
import {
  NonInteractiveVisualizationWrapper,
  TitleSectionDataHandler,
} from '@mentimeter/question-modules-shared';
import type { ScalesResultT } from '@mentimeter/visualizations';
import { useMemo } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { ScreenReaderResults } from './ScreenReaderResults/ScreenReaderResults';
import { TrendsDataHandler } from './visualizations/Trends';
import { ScalesVisualizationDataHandler } from './visualizations/ScalesVisualizationDataHandler';
import { SpiderVisualizationDataHandler } from './visualizations/SpiderVisualizationDataHandler';

interface ScalesPresentationProps {
  choices: Question['choices'];
  range: Question['range'];
  dimensionLabels: { min: string; max: string };
  sub_type: Question['sub_type'];
  results: ScalesResultT[];
  numberOfRespondents: number;
  shouldHideResults: boolean;
  shouldShowTrends: boolean;
  shouldShowOverallAverage: boolean;
  identifiedResults: ScaleResult[] | undefined;
  shouldAnimate: boolean;
  CanvasVisualizationArea?: InteractiveCanvasComponents['CanvasVisualizationArea'];
}

export const ScalesPresentation = ({
  choices,
  range,
  dimensionLabels,
  sub_type,
  results,
  numberOfRespondents,
  shouldHideResults,
  shouldShowTrends,
  shouldShowOverallAverage,
  identifiedResults,
  shouldAnimate,
  CanvasVisualizationArea,
}: ScalesPresentationProps) => {
  const Visualization = useMemo(() => {
    if (sub_type === 'spider') {
      return SpiderVisualizationDataHandler;
    }
    return ScalesVisualizationDataHandler;
  }, [sub_type]);

  const resultsWithFallback = choices.map((choice) => {
    const result = results.find((result) => result.id === choice.id);
    const fallbackResult = {
      id: choice.id,
      label: choice.label,
      score: {},
    } satisfies ScalesResultT;
    return result ?? fallbackResult;
  });

  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="scales-container"
      alignItems="center"
      justifyContent="center"
    >
      <TitleSectionDataHandler showTextSizeDropdown={true} />
      {shouldShowTrends ? (
        <Box flex="1" width="100%">
          <TrendsDataHandler
            choices={choices}
            range={range}
            shouldHideResults={shouldHideResults}
          />
        </Box>
      ) : (
        <ConditionalWrapper flex="1" width="100%">
          <>
            <Visualization
              results={resultsWithFallback}
              numberOfRespondents={numberOfRespondents}
              range={range}
              choices={choices}
              dimensionLabels={dimensionLabels}
              shouldShowOverallAverage={shouldShowOverallAverage}
              identifiedResults={identifiedResults}
              shouldHideResults={shouldHideResults}
              shouldAnimate={shouldAnimate}
            />
            <ScreenReaderOnly>
              <ScreenReaderResults
                results={results}
                range={range}
                dimensionLabels={dimensionLabels}
                shouldHideResults={shouldHideResults}
              />
            </ScreenReaderOnly>
          </>
        </ConditionalWrapper>
      )}
    </Box>
  );
};
